import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  UseLayoutPagesProps,
  PageType,
  PageItemType,
  LayoutDimensions,
} from "../types";
import {
  deriveLayoutDimensions,
  makePageItem,
  makePlaceholderPageItem,
} from "../utils";

export type UseLayoutPagesReturn = {
  pages: PageType[];
  currentPage: number;
  addEmptyPage(): void;
  goToPage(page: number): void;
  goToPreviousPage(): void;
  goToNextPage(): void;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  layoutDimensions?: LayoutDimensions;
};

const DEFAULT_NUM_PAGES = 1;

export function useLayoutPages({
  isEditing,
  layoutNodes,
  width,
  onPageChanged,
}: UseLayoutPagesProps): UseLayoutPagesReturn {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const numPages = useRef<number>(DEFAULT_NUM_PAGES);
  const minimumPages = useRef<number>(DEFAULT_NUM_PAGES);

  const layoutDimensions = useMemo(() => {
    return width ? deriveLayoutDimensions(width) : undefined;
  }, [width]);

  function derivePages() {
    const newPages: PageType[] = [];

    if (!layoutDimensions) return newPages;

    // find maximum position of items
    const maxPosition = layoutNodes.reduce((acc, item) => {
      return item.position > acc ? item.position : acc;
    }, 0);

    // find number of pages
    minimumPages.current =
      Math.ceil((maxPosition + 1) / layoutDimensions.itemsPerPage) || 1;

    const newNumPages = isEditing ? numPages.current : minimumPages.current;

    if (newNumPages >= numPages.current) {
      numPages.current = newNumPages;
    }

    // create pages
    for (let pageIndex = 1; pageIndex <= newNumPages; pageIndex++) {
      const newPage: PageType = {
        page: pageIndex,
        items: [],
      };

      for (
        let localIndex = 0;
        localIndex < layoutDimensions.itemsPerPage;
        localIndex++
      ) {
        const newPosition =
          localIndex + (pageIndex - 1) * layoutDimensions.itemsPerPage;
        const item = layoutNodes.find((item) => item.position === newPosition);
        let pageItemType: PageItemType | undefined = undefined;
        if (item) {
          pageItemType = makePageItem(item) as PageItemType;
          newPage.items.push(pageItemType);
        } else {
          pageItemType = makePlaceholderPageItem(newPosition);
          newPage.items.push(pageItemType);
        }
      }

      newPages.push(newPage);
    }

    return newPages;
  }

  function goToPage(page: number) {
    if (page > 0) {
      setCurrentPage(page);
      requestAnimationFrame(() => onPageChanged(page));
    }
  }

  function addEmptyPage() {
    numPages.current = numPages.current + 1;
    forceUpdate(); // force update to trigger re-render
    setTimeout(() => {
      setCurrentPage(numPages.current);
      onPageChanged(numPages.current);
    }, 0);
  }

  useEffect(() => {
    if (!isEditing) {
      numPages.current = minimumPages.current;
      if (currentPage > numPages.current) {
        goToPage(numPages.current);
        setCurrentPage(numPages.current);
      }
    }
  }, [isEditing]);

  const pages = useMemo(() => {
    return derivePages();
  }, [derivePages, width, layoutNodes, currentPage]);

  const hasPreviousPage = currentPage > 1;

  const hasNextPage = currentPage < pages.length;

  const r = useMemo(() => {
    return {
      pages,
      layoutDimensions,
      currentPage,
      addEmptyPage,
      goToPage,
      goToPreviousPage: () => {
        setCurrentPage((prev) => {
          const next = prev - 1;
          if (next <= 0) return prev;
          requestAnimationFrame(() => onPageChanged(next));
          return next;
        });
      },
      goToNextPage: () => {
        setCurrentPage((prev) => {
          const next = prev + 1;
          if (next > pages.length) return prev;
          requestAnimationFrame(() => onPageChanged(next));
          return next;
        });
      },
      hasPreviousPage,
      hasNextPage,
    };
  }, [
    pages,
    layoutDimensions,
    currentPage,
    addEmptyPage,
    goToPage,
    hasPreviousPage,
    hasNextPage,
  ]);

  return r;
}
